/**
 * ProductGroupIcon — the round product glyph of a product group: the 24px disc
 * the icon-layout catalogue cards carry instead of a thumbnail, and the 32px one
 * beside the product drawer's title.
 *
 * Documents have no artwork BY DESIGN, not for want of an export: the designer's
 * prototype gives their cards a product icon on a beige disc and a narrower
 * 3-up grid, and reserves thumbnails for the visual products. The glyphs are
 * lifted verbatim from that prototype's own `ICONS` map, viewBox included, so
 * they are the designer's paths rather than a lookalike from an icon set.
 *
 * Sizing mirrors `.bkbn-producticon[data-size="md"|"lg"]`: a 24px disc around a
 * 16px glyph, or 32px around 21px. The glyph data itself lives in
 * `constants/purchaseFlowRevamp/productGlyphs` — shared with `ProductKindIcon`,
 * which draws the same drawings bare beside a bundle member.
 */

import React from 'react'
import { Box } from '@mui/material'

import { BEIGE_600, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { PRODUCT_GLYPHS, type ProductGlyphName } from 'constants/purchaseFlowRevamp/productGlyphs'
import { ProductGlyphSvg } from './ProductGlyphSvg.component'

/**
 * Which glyph a product group gets, keyed by the Salesforce WorkType code the
 * catalogue publishes. Where the prototype shows the group, the glyph is the one
 * it gives that product; the rest follow their family (every video is the play
 * button, every Matterport floor plan the plan).
 */
const GLYPH_BY_GROUP_CODE: Readonly<Record<string, ProductGlyphName>> = {
  // photography
  GROUND_PHOTO: 'photo',
  DRONE_PHOTO: 'drone',
  EXTERIOR_PHOTO: 'photo',
  MULTI_FLAT_PHOTOS: 'photo',
  PRESTIGE_PHOTO: 'photo',
  MATTERPORT_PHOTO: 'photo',
  MAST_PHOTO: 'photo',
  AI_EDITING_PHOTO_PURCHASE_FLOW: 'aiEditing',
  PHOTO_EDITING: 'editing',
  PHOTO_BLURRING: 'editing',
  // videos
  GROUND_VIDEO: 'video',
  DRONE_VIDEO: 'drone',
  GROUND_DRONE_VIDEO: 'video',
  TEASER_VIDEO: 'video',
  SLIDESHOW: 'video',
  REEL_VIDEO: 'video',
  PRESTIGE_VIDEO: 'video',
  TIMELAPSE: 'video',
  // virtual tours
  VIRTUAL_TOUR_MATTERPORT: 'virtualVisit',
  VIRTUAL_TOUR_MATTERPORT_SD: 'virtualVisit',
  VIRTUAL_TOUR_FLOORFY: 'virtualVisit',
  '360_PHOTO_FOR_VIRTUAL_TOUR': 'virtualVisit',
  // floor plans
  MOBILE_APPLICATION_FLOOR_PLAN: 'floorPlan',
  '2D_FLOOR_PLAN_EDITING': 'editing',
  '3D_FLOOR_PLAN_EDITING': 'editing',
  ON_SITE_AREA_REPORT: 'floorPlan',
  LIVING_AREA_CALCULATION: 'floorPlan',
  FLOOR_PLAN_MATTERPORT_WITH_VISIT: 'floorPlan',
  FLOOR_PLAN_MATTERPORT_WITHOUT_VISIT: 'floorPlan',
  FLOOR_PLAN_RENDERING_3D: 'rendering',
  // 3D
  STAGING: 'staging',
  '360_STAGING': 'staging',
  RENDERING: 'rendering',
  COMMERCIAL_RENDERING: 'rendering',
  // documents
  AUTHORITIES_DOCUMENT: 'authoritiesDocument',
  CONSTRUCTION_FILE: 'authoritiesDocument',
  DOCUMENTATION: 'authoritiesDocument',
  LAND_REGISTRATION: 'authoritiesDocument',
  LAW_INQUIRIES: 'authoritiesDocument',
  MUNICIPAL: 'authoritiesDocument',
  PROTECTION: 'authoritiesDocument',
  ENERGY_CERTIFICATE: 'energyCertificate',
  CONSUMPTION_CERTIFICATE: 'energyCertificate',
}

/** The glyph for a group, or `undefined` when the group has none mapped. */
export const glyphNameForGroup = (productGroupCode: string | undefined): ProductGlyphName | undefined =>
  productGroupCode ? GLYPH_BY_GROUP_CODE[productGroupCode] : undefined

const SIZES = {
  md: { disc: '2.4rem', glyph: '1.6rem', color: GRAY_900 },
  lg: { disc: '3.2rem', glyph: '2.1rem', color: GRAY_800 },
} as const

interface Props {
  /** BE `productGroupCode` of the shell product. */
  productGroupCode?: string
  /** `md` on a catalogue card, `lg` beside the drawer title. */
  size?: keyof typeof SIZES
  /** Disc colour: beige on a card, white on the drawer's beige header. */
  background?: 'beige' | 'white'
  /**
   * Glyph for an unmapped group. The card falls back to the document glyph —
   * the icon layout only renders for document-shaped categories — while the
   * drawer passes `null` and so renders nothing rather than guess at a service.
   */
  fallback?: ProductGlyphName | null
}

/**
 * @example
 * <ProductGroupIcon productGroupCode="ENERGY_CERTIFICATE" />
 * <ProductGroupIcon productGroupCode={code} size="lg" background="white" fallback={null} />
 */
export const ProductGroupIcon: React.FC<Props> = ({
  productGroupCode,
  size = 'md',
  background = 'beige',
  fallback = 'authoritiesDocument',
}) => {
  const name = glyphNameForGroup(productGroupCode) ?? fallback
  if (!name) return null

  const { disc, glyph, color } = SIZES[size]

  return (
    <Box
      aria-hidden
      data-cy="pf-revamp-product-group-icon"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: disc,
        height: disc,
        borderRadius: '50%',
        backgroundColor: background === 'white' ? WHITE : BEIGE_600,
        color,
      }}
    >
      <ProductGlyphSvg glyph={PRODUCT_GLYPHS[name]} sx={{ display: 'block', width: glyph, height: glyph }} />
    </Box>
  )
}
